import React, { useState, useEffect } from 'react';
import { Bell, Plus, Edit, Trash2, AlertCircle, CheckCircle, Search, Image as ImageIcon, Video, MessageSquare, Building, Send } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'text' | 'image' | 'video';
  media_url: string | null;
  link_url: string | null;
  is_read: boolean;
  created_at: string;
  user_id: string;
  igreja_id: string;
  igreja_nome?: string;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

interface NotificationFormData {
  title: string;
  message: string;
  type: 'text' | 'image' | 'video';
  media_url: string;
  link_url: string;
  igreja_id: string;
  send_to_all: boolean;
}

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [notificationToDelete, setNotificationToDelete] = useState<Notification | null>(null);
  const [formData, setFormData] = useState<NotificationFormData>({
    title: '',
    message: '',
    type: 'text',
    media_url: '',
    link_url: '',
    igreja_id: '',
    send_to_all: false
  });
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('');

  useEffect(() => {
    fetchNotifications();
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort notifications
    let filtered = [...notifications];
    
    // Apply igreja filter
    if (selectedIgreja) {
      filtered = filtered.filter(notification => notification.igreja_id === selectedIgreja);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(notification => 
        notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.igreja_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'igreja_nome') {
          aValue = a.igreja_nome || '';
          bValue = b.igreja_nome || '';
        } else {
          aValue = a[sortField as keyof Notification];
          bValue = b[sortField as keyof Notification];
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredNotifications(filtered);
  }, [notifications, searchTerm, sortField, sortOrder, selectedIgreja]);

  async function fetchNotifications() {
    try {
      setLoading(true);
      
      // Fetch notifications
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*');
      
      if (notificationsError) throw notificationsError;
      
      // Fetch igrejas to get names
      const { data: igrejasData, error: igrejasError } = await supabase
        .from('igrejas')
        .select('id, nome');
      
      if (igrejasError) throw igrejasError;
      
      // Map igreja names to notifications
      const notificationsWithIgrejaNames = notificationsData?.map(notification => {
        const igreja = igrejasData?.find(i => i.id === notification.igreja_id);
        return {
          ...notification,
          igreja_nome: igreja?.nome || 'Todas as Igrejas'
        };
      }) || [];
      
      setNotifications(notificationsWithIgrejaNames);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar notificações');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIgrejas() {
    try {
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const { title, message, type, media_url, link_url, igreja_id, send_to_all } = formData;
      
      if (editingNotification) {
        // Update existing notification
        const { error } = await supabase
          .from('notifications')
          .update({ 
            title, 
            message, 
            type, 
            media_url: media_url || null, 
            link_url: link_url || null,
            igreja_id: send_to_all ? null : igreja_id
          })
          .eq('id', editingNotification.id);
        
        if (error) throw error;
        setSuccess('Notificação atualizada com sucesso!');
      } else {
        // Create new notification
        const { error } = await supabase
          .from('notifications')
          .insert([{ 
            title, 
            message, 
            type, 
            media_url: media_url || null, 
            link_url: link_url || null,
            igreja_id: send_to_all ? null : igreja_id,
            is_read: false
          }]);
        
        if (error) throw error;
        
        // Send push notification (this would be implemented with Firebase Cloud Messaging)
        // For now, we'll just show a success message
        setSuccess('Notificação criada e enviada com sucesso!');
      }
      
      // Reset form and close modal
      setFormData({
        title: '',
        message: '',
        type: 'text',
        media_url: '',
        link_url: '',
        igreja_id: '',
        send_to_all: false
      });
      setIsModalOpen(false);
      setEditingNotification(null);
      
      // Refresh data
      fetchNotifications();
    } catch (error: any) {
      setError(error.message || 'Erro ao salvar notificação');
    }
  };

  const handleEdit = (notification: Notification) => {
    setEditingNotification(notification);
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      media_url: notification.media_url || '',
      link_url: notification.link_url || '',
      igreja_id: notification.igreja_id || '',
      send_to_all: !notification.igreja_id
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!notificationToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Delete notification
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Notificação excluída com sucesso!');
      setIsDeleteModalOpen(false);
      setNotificationToDelete(null);
      
      // Refresh data
      fetchNotifications();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir notificação');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-5 w-5 text-blue-600" />;
      case 'video':
        return <Video className="h-5 w-5 text-red-600" />;
      default:
        return <MessageSquare className="h-5 w-5 text-purple-600" />;
    }
  };

  const columns = [
    {
      header: 'Notificação',
      accessorKey: 'title',
      enableSorting: true,
      cell: (info: { row: Notification }) => (
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${
            info.row.type === 'image' ? 'bg-blue-100' :
            info.row.type === 'video' ? 'bg-red-100' :
            'bg-purple-100'
          }`}>
            {getTypeIcon(info.row.type)}
          </div>
          <div>
            <div className="font-medium text-gray-900">{info.row.title}</div>
            <div className="text-sm text-gray-500 line-clamp-1">{info.row.message}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Igreja',
      accessorKey: 'igreja_nome',
      enableSorting: true,
    },
    {
      header: 'Tipo',
      accessorKey: 'type',
      enableSorting: true,
      cell: (info: { row: Notification }) => {
        let badge;
        
        switch (info.row.type) {
          case 'image':
            badge = <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">Imagem</span>;
            break;
          case 'video':
            badge = <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">Vídeo</span>;
            break;
          default:
            badge = <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">Texto</span>;
        }
        
        return badge;
      }
    },
    {
      header: 'Enviada em',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (info: { row: Notification }) => (
        <div>{formatDate(info.row.created_at)}</div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: Notification }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleEdit(info.row)}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Editar"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setNotificationToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredNotifications.length / pageSize);
  const paginatedData = filteredNotifications.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Gerenciar Notificações">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Igreja Filter */}
      <div className="mb-6">
        <label htmlFor="igreja_filter" className="block text-sm font-medium text-gray-700 mb-1">
          Filtrar por Igreja
        </label>
        <select
          id="igreja_filter"
          value={selectedIgreja}
          onChange={(e) => setSelectedIgreja(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
        >
          <option value="">Todas as Igrejas</option>
          {igrejas.map((igreja) => (
            <option key={igreja.id} value={igreja.id}>
              {igreja.nome} - {igreja.cidade}/{igreja.estado}
            </option>
          ))}
        </select>
      </div>

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Notificações"
        subtitle={`Total de ${filteredNotifications.length} notificações enviadas`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="notificacoes"
        actions={
          <button
            onClick={() => {
              setEditingNotification(null);
              setFormData({
                title: '',
                message: '',
                type: 'text',
                media_url: '',
                link_url: '',
                igreja_id: '',
                send_to_all: false
              });
              setIsModalOpen(true);
            }}
            className="px-3 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Nova Notificação</span>
          </button>
        }
      />

      {/* Notification Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-brand-teal mb-6">
              {editingNotification ? 'Editar Notificação' : 'Nova Notificação'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Mensagem *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Notificação *
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  >
                    <option value="text">Texto</option>
                    <option value="image">Imagem</option>
                    <option value="video">Vídeo</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="send_to_all" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <input
                      type="checkbox"
                      id="send_to_all"
                      name="send_to_all"
                      checked={formData.send_to_all}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300 text-brand-teal focus:ring-brand-teal"
                    />
                    Enviar para todas as igrejas
                  </label>
                </div>
                
                {!formData.send_to_all && (
                  <div className="md:col-span-2">
                    <label htmlFor="igreja_id" className="block text-sm font-medium text-gray-700 mb-1">
                      Igreja *
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <select
                        id="igreja_id"
                        name="igreja_id"
                        value={formData.igreja_id}
                        onChange={handleInputChange}
                        required={!formData.send_to_all}
                        className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                      >
                        <option value="">Selecione uma igreja...</option>
                        {igrejas.map((igreja) => (
                          <option key={igreja.id} value={igreja.id}>
                            {igreja.nome} - {igreja.cidade}/{igreja.estado}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                
                {formData.type !== 'text' && (
                  <div className="md:col-span-2">
                    <label htmlFor="media_url" className="block text-sm font-medium text-gray-700 mb-1">
                      URL da {formData.type === 'image' ? 'Imagem' : 'Vídeo'} *
                    </label>
                    <input
                      type="url"
                      id="media_url"
                      name="media_url"
                      value={formData.media_url}
                      onChange={handleInputChange}
                      required={formData.type !== 'text'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                      placeholder={formData.type === 'image' ? 'https://exemplo.com/imagem.jpg' : 'https://youtube.com/embed/video_id'}
                    />
                  </div>
                )}
                
                <div className="md:col-span-2">
                  <label htmlFor="link_url" className="block text-sm font-medium text-gray-700 mb-1">
                    URL de Redirecionamento
                  </label>
                  <input
                    type="url"
                    id="link_url"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    placeholder="https://exemplo.com/pagina"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    URL para onde o usuário será redirecionado ao clicar na notificação.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {editingNotification ? 'Atualizar' : 'Enviar Notificação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && notificationToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir a notificação <strong>{notificationToDelete.title}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setNotificationToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}